import { createEffect, createSignal, onCleanup, onMount, Show } from "solid-js";
import { Outlet, useLocation, useRouterState } from "@tanstack/solid-router";
import type { AbstractPowerSyncDatabase } from "@powersync/common";

import { appState, useInteractionFlag } from "@fleet/ui";
import { useTheme } from "@/providers/theme-provider";
import { useSession } from "@/lib/auth";
import { resetUndoScope } from "@/lib/undo";

// Layout specific components and providers
import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";
import { ShortcutsModal } from "@/components/shortcuts-modal";

// Layout module parts
import { AppLayoutSidebar } from "./app-layout-sidebar";
import { useAppShortcuts } from "../hooks/use-app-shortcuts";
import { ContentSkeleton } from "@/components/content-skeleton";
import { AccountSwitcherHost } from "@/features/auth/account-switcher-host";

interface AppLayoutContentProps {
  // Nullable: the shell mounts immediately, before PowerSync's local DB has
  // finished initializing. Data-bound regions render a skeleton until it exists.
  db: AbstractPowerSyncDatabase | null;
}

function useGlobalEventListeners(
  isSidebarOpen: () => boolean,
  setIsSidebarOpen: (open: boolean) => void,
) {
  // Keyboard-vs-pointer activity tracking (and the `body.keyboard-navigation`
  // class) is owned by <InteractionProvider>; only the sidebar-specific Escape
  // handler remains here.
  onMount(() => {
    const handleEscapeForSidebar = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };

    globalThis.addEventListener("keydown", handleEscapeForSidebar);

    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscapeForSidebar);
    });
  });
}

export function AppLayoutContent(props: AppLayoutContentProps) {
  onMount(() => {
    // Notify the InitialSyncOverlay that we are mounted and ready
    appState.markAsReady();
  });

  const location = useLocation();
  const routerState = useRouterState();
  const session = useSession();

  // UNDO-TRASH-LIGHT L1: the undo stack NEVER crosses accounts. When changing
  // the active account (multi-account #18, login/logout, guest→user) it is cleared
  // and the pill disappears — an inverse must never write to another session.
  createEffect(() => {
    resetUndoScope(session.userId());
  });

  const isNavigating = () => routerState().status === "pending";

  const theme = useTheme();

  const [isSidebarOpen, setIsSidebarOpen] = createSignal(false);
  const [shortcutsOpen, setShortcutsOpen] = createSignal(false);

  // Close the mobile sidebar whenever the route changes.
  let lastPath = location().pathname;
  const closeSidebarOnNavigate = () => {
    const current = location().pathname;
    if (current !== lastPath) {
      lastPath = current;
      setIsSidebarOpen(false);
    }
  };

  useAppShortcuts({
    theme,
    shortcutsOpen,
    setShortcutsOpen,
    setIsSidebarOpen,
  });

  // ── Keyboard-navigation visual indicator + touch gestures
  useGlobalEventListeners(isSidebarOpen, setIsSidebarOpen);

  // With the nav drawer open, the horizontal gesture belongs to it (drag to
  // dismiss). There is no page-level swipe gesture bound to the window in this
  // repo yet, so nothing needs to stand down today — but the flag is the
  // declaration of who owns horizontal swiping, and the day this repo grows one
  // it only needs to check the flag, not re-derive DOM ownership.
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  const glows = () => {
    const toks = theme.tokens();
    if (toks && toks.id !== "none") {
      return { glow0: toks.glow0, glow1: toks.glow1, glow2: toks.glow2 };
    }
    return { glow0: "var(--glow-0)", glow1: "var(--glow-1)", glow2: "var(--glow-2)" };
  };

  return (
    <AppBackground
      class="flex h-dvh text-foreground overflow-hidden"
      isBlur={theme.isBlur()}
      patternType={theme.tokens()?.patternType}
      patternOpacity={theme.tokens()?.patternOpacity}
      {...glows()}
    >
      <AppLayoutSidebar isOpen={isSidebarOpen} setOpen={setIsSidebarOpen} />

      {/* ── Main content ───────────────────────────────────────── */}
      <main class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)]">
        <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
        <OfflineBanner />
        <div class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]">
          <div class="flex flex-1 flex-col min-h-0 h-full">
            {(() => {
              closeSidebarOnNavigate();
              return null;
            })()}
            {/* The static shell above mounts immediately. The content region is
                the only data-bound region in the shell: route views fetch from
                PowerSync via usePowerSyncContext, so show a granular skeleton
                until the local DB is available. */}
            <Show when={props.db} fallback={<ContentSkeleton />}>
              {/* Outlet-level entrance: applied ONCE here so every route slides
                  in consistently. Keyed by `pathname` only (search params are
                  excluded) so filtering/searching never re-triggers it. This
                  wrapper is a SEPARATE node from any per-page swipe transform
                  (e.g. planner week swipe) to avoid colliding transforms. */}
              <Show when={location().pathname} keyed>
                <div class="flex flex-1 flex-col min-h-0 h-full animate-slide-in-up will-change-[transform,opacity]">
                  <Outlet />
                </div>
              </Show>
            </Show>
          </div>
        </div>
      </main>

      <ShortcutsModal
        isOpen={shortcutsOpen()}
        onClose={() => {
          setShortcutsOpen(false);
        }}
      />

      {/* Multi-cuenta (#18): el overlay del switcher se monta UNA sola vez a
          nivel layout (la sidebar se renderiza dos veces — drawer móvil + raíl
          escritorio — así evitamos dos overlays apilados). Es addressable por
          URL (`?account=switch`); los triggers de cada sidebar sólo cambian la
          URL. */}
      <AccountSwitcherHost />
    </AppBackground>
  );
}
